/**
 * @module-isolated analytics-cohort
 * @depends api.js (vía cohort-service)
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ANALYTICS_CONFIG, PERIOD_OPTIONS, resolvePeriodRange } from '../../../config/analytics-config.js';
import { mapAnalyticsSecurityError } from '../../../services/firebase-auth.js';
import { useOriginTagsCatalog } from '../../../shared/hooks/useOriginTagsCatalog.js';
import { cacheGet, cacheInvalidate, cacheSet } from '../../../shared/utils/session-cache.js';
import { useAnalyticsRefresh } from '../../../shared/hooks/useAnalyticsRefresh.js';
import { fetchMetaCampaigns } from '../../../services/meta-api.js';
import { loadCohortNow, syncCohortSource } from '../services/cohort-service.js';
import { ORIGIN_META, uniqueByLeadId } from '../utils/filter-cohort.js';

const PERIOD_OPTIONS_WITH_CUSTOM = PERIOD_OPTIONS.concat([{ key: 'custom', label: 'Personalizado' }]);

function ymdToday() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return d.getFullYear() + '-' + m + '-' + day;
}

function resolveRange(period, dateFrom, dateTo) {
  if (period === 'custom' && dateFrom && dateTo) {
    return { dateFrom: dateFrom, dateTo: dateTo };
  }
  return resolvePeriodRange(period === 'custom' ? ANALYTICS_CONFIG.defaultPeriod : period);
}

function cacheKey(period, dateFrom, dateTo) {
  const r = resolveRange(period, dateFrom, dateTo);
  return 'cohort:' + r.dateFrom + '|' + r.dateTo;
}

export function useCohortData() {
  const [period, setPeriodState] = useState(ANALYTICS_CONFIG.defaultPeriod);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [syncBusy, setSyncBusy] = useState(false);
  const [originIds, setOriginIds] = useState([ORIGIN_META]);
  const [campaignIds, setCampaignIds] = useState([]);
  const [allMetaCampaigns, setAllMetaCampaigns] = useState(true);
  const [metaAdsCampaigns, setMetaAdsCampaigns] = useState([]);

  const originCatalog = useOriginTagsCatalog({ persistKey: 'cohort-catalog' });

  useEffect(() => {
    let cancelled = false;
    fetchMetaCampaigns()
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res && res.data) ? res.data : [];
        setMetaAdsCampaigns(
          rows
            .filter((row) => String((row && row.effectiveStatus) || '').toUpperCase() === 'ACTIVE')
            .map((row) => ({
              id: String((row && row.id) || '').trim(),
              name: String((row && (row.name || row.id)) || '').trim()
            }))
            .filter((row) => row.id && row.name)
        );
      })
      .catch(() => {
        if (!cancelled) setMetaAdsCampaigns([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  const loadSeq = useRef(0);
  const hasSearched = useRef(false);

  const setPeriod = useCallback((p) => {
    setPeriodState(p);
    if (p === 'custom') {
      setDateTo((prev) => prev || ymdToday());
    } else {
      setDateFrom('');
      setDateTo('');
    }
  }, []);

  const range = useMemo(
    () => resolveRange(period, dateFrom, dateTo),
    [period, dateFrom, dateTo]
  );
  const customReady = period !== 'custom' || (!!dateFrom && !!dateTo);
  const periodLabel = useMemo(() => {
    if (period === 'all') return 'Todos';
    if (range && range.dateFrom && range.dateTo) {
      if (range.dateFrom === range.dateTo) return range.dateFrom;
      return range.dateFrom + ' → ' + range.dateTo;
    }
    return 'período';
  }, [period, range]);

  const load = useCallback(
    async (opts) => {
      if (!customReady) return;
      const force = !!(opts && opts.force);
      const key = cacheKey(period, dateFrom, dateTo);
      const r = resolveRange(period, dateFrom, dateTo);
      const seq = ++loadSeq.current;
      hasSearched.current = true;
      if (!force) {
        const hit = cacheGet(key);
        if (hit && hit.data) {
          if (seq !== loadSeq.current) return;
          setData(hit.data);
          setLoading(false);
          setError('');
          return;
        }
      } else {
        cacheInvalidate(key);
      }
      setLoading(true);
      setError('');
      try {
        const payload = await loadCohortNow(r);
        if (seq !== loadSeq.current) return;
        if (payload && payload.success === false) {
          throw new Error(payload.error || payload.message || 'No se pudo cargar la cohorte.');
        }
        const next = payload && payload.cohortLeads
          ? payload
          : payload && payload.data && payload.data.cohortLeads
            ? payload.data
            : payload;
        if (!next || !Array.isArray(next.cohortLeads)) {
          throw new Error('La API no devolvió cohortLeads. Revisá el deploy de analytics-api.');
        }
        next.cohortLeads = uniqueByLeadId(next.cohortLeads);
        cacheSet(key, next);
        setData(next);
      } catch (err) {
        if (seq !== loadSeq.current) return;
        console.error('[cohort] load', err);
        setError(mapAnalyticsSecurityError(err) || (err && err.message) || 'Error al cargar.');
      } finally {
        if (seq === loadSeq.current) setLoading(false);
      }
    },
    [period, dateFrom, dateTo, customReady]
  );

  useAnalyticsRefresh(
    useCallback(() => {
      if (hasSearched.current) load({ force: true });
    }, [load])
  );

  const reload = useCallback(() => {
    load({ force: true });
  }, [load]);

  const cancelSearch = useCallback(() => {
    loadSeq.current += 1;
    setLoading(false);
  }, []);

  const runSync = useCallback(async () => {
    setSyncBusy(true);
    setError('');
    try {
      const res = await syncCohortSource({ tipo: 'full' });
      if (!res || res.ok === false || res.success === false) {
        throw new Error((res && (res.error || res.message)) || 'Sync falló.');
      }
      cacheInvalidate(cacheKey(period, dateFrom, dateTo));
      await load({ force: true });
    } catch (err) {
      console.error('[cohort] sync', err);
      setError(mapAnalyticsSecurityError(err) || (err && err.message) || 'Error al sincronizar.');
    } finally {
      setSyncBusy(false);
    }
  }, [load, period, dateFrom, dateTo]);

  const setDateFromAndLoad = useCallback((v) => {
    setDateFrom(v);
  }, []);

  const setDateToAndLoad = useCallback((v) => {
    setDateTo(v);
  }, []);

  const nonMetaOriginOptions = useMemo(() => {
    return (originCatalog.options || []).filter((o) => o.isMeta !== true);
  }, [originCatalog.options]);

  return {
    period,
    setPeriod,
    periodOptions: PERIOD_OPTIONS_WITH_CUSTOM,
    dateFrom,
    setDateFrom: setDateFromAndLoad,
    dateTo,
    setDateTo: setDateToAndLoad,
    range,
    periodLabel,
    customReady,
    loading,
    awaitingOrigins: !originCatalog.ready,
    awaitingCustomDates: period === 'custom' && !customReady,
    error,
    data,
    syncBusy,
    reload,
    cancelSearch,
    runSync,
    originIds,
    setOriginIds,
    campaignIds,
    setCampaignIds,
    allMetaCampaigns,
    setAllMetaCampaigns,
    nonMetaOriginOptions,
    originCatalog,
    metaAdsCampaigns
  };
}
